import { Edit } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import {
  SimpleColumn,
  SimpleSortableTable,
} from "../../../../../components/ui/simple-sortable-table";
import { ImileFranquia } from "../types";

interface FranquiasTableProps {
  franquias: ImileFranquia[];
  onEditClick: (franquia: ImileFranquia) => void;
}

export function FranquiasTable({
  franquias,
  onEditClick,
}: FranquiasTableProps) {
  if (franquias.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground">
              Nenhuma franquia iMile cadastrada
            </h2>
            <p className="text-muted-foreground mt-2">
              Cadastre a primeira franquia usando o formulário acima. Sem
              cadastro, os romaneios da iMile não entram no checkout.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: SimpleColumn<ImileFranquia>[] = [
    {
      key: "cnpj",
      label: "CNPJ",
      cellClassName: "font-medium",
    },
    {
      key: "razaoSocial",
      label: "Razão Social",
    },
    {
      key: "apelido",
      label: "Prefixo",
      render: (franquia) => (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-primary/10 text-primary">
          {franquia.apelido}
        </span>
      ),
    },
    {
      key: "ds",
      label: "DS",
      render: (franquia) => (
        <span className="font-mono text-xs">{franquia.ds || "-"}</span>
      ),
    },
    {
      // A coluna que de fato importa para conferir o cadastro: é com esses nomes
      // que o romaneio é gravado e que o checkout procura.
      key: "opsDerivadas",
      label: "Ops geradas",
      sortable: false,
      render: (franquia) =>
        franquia.opsDerivadas.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {franquia.opsDerivadas.map((ops) => (
              <span
                key={ops}
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-mono text-xs"
              >
                {ops}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: "emailContato",
      label: "Email Contato",
    },
    {
      key: "cep",
      label: "CEP",
    },
    {
      key: "endereco",
      label: "Endereço",
    },
    {
      key: "redutor",
      label: "Redutor",
      sortValue: (franquia) => franquia.redutor ?? 1,
      render: (franquia) => (
        <span className="font-semibold text-primary">
          {franquia.redutor !== undefined
            ? franquia.redutor.toFixed(2)
            : "1.00"}
        </span>
      ),
    },
    {
      key: "nfsAtivo",
      label: "NFS Ativo",
      sortValue: (franquia) => Boolean(franquia.nfsAtivo),
      render: (franquia) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            franquia.nfsAtivo
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {franquia.nfsAtivo ? "Ativo" : "Inativo"}
        </span>
      ),
    },
    {
      key: "criadoPor",
      label: "Criado Por",
    },
    {
      key: "dataCriacao",
      label: "Data Criação",
    },
    {
      key: "acoes",
      label: "Ações",
      sortable: false,
      headClassName: "text-right",
      cellClassName: "text-right",
      render: (franquia) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEditClick(franquia)}
        >
          <Edit className="h-4 w-4 mr-1" />
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Franquias iMile Cadastradas ({franquias.length})
        </h2>
        <div className="overflow-x-auto">
          <SimpleSortableTable
            columns={columns}
            rows={franquias}
            rowKey={(franquia) => franquia.id}
          />
        </div>
      </CardContent>
    </Card>
  );
}
